import { useContext, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import CourseService from "../../../../services/CourseService";
import { Skeleton } from "primereact/skeleton";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TotalLearnersChart() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);

  const [labels, setLabels] = useState([]);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalLearners, setTotalLearners] = useState(0);
  const [selectedCourse, setSelectedCourse] = useState("All");
 const [courses, setCourses] = useState([]);
  const [originalData, setOriginalData] = useState([]); 

  // Fetch once
useEffect(() => {
    const controller = new AbortController();

    const fetchLearnerData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await CourseService.GetLearnersDashboard(
          authContext,
          controller.signal
        );

        if (controller.signal.aborted) return;

        if (response && response.isSuccess && Array.isArray(response.data)) {
          setCourses([{ courseName: "All", courseCode: "All" }, ...response.data]);
          setOriginalData(response.data);
          const fetchedLabels = response.data.map((item) => item.courseName || "");
          const fetchedCounts = response.data.map((item) => item.count || 0);
          setLabels(fetchedLabels);
          setCounts(fetchedCounts);
          setTotalLearners(fetchedCounts.reduce((acc, val) => acc + val, 0));
        } else {
          setError("Failed to load learners data: " + (response?.message || "Unknown error"));
          setCourses([]);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to load learners data: " + err.message);
        setCourses([]);
        console.error("Learners dashboard API error:", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchLearnerData();

    return () => controller.abort(); 
  }, [authContext]);

   // Filter data on course change
  useEffect(() => {
    if (selectedCourse === "All") {
      const fetchedLabels = originalData.map((item) => item.courseName || "");
      const fetchedCounts = originalData.map((item) => item.count || 0);

      setLabels(fetchedLabels);
      setCounts(fetchedCounts);
      setTotalLearners(fetchedCounts.reduce((acc, val) => acc + val, 0));
    } else {
      const filtered = originalData.filter(
        (item) => item.courseCode === selectedCourse
      );

      const fetchedLabels = filtered.map((item) => item.courseName || "");
      const fetchedCounts = filtered.map((item) => item.count || 0);

      setLabels(fetchedLabels);
      setCounts(fetchedCounts);
      setTotalLearners(fetchedCounts.reduce((acc, val) => acc + val, 0));
    }
  }, [selectedCourse, originalData]);

  const getAbbreviation = (name) => {
    if (!name) return "";
    const skipWords = ["and", "with", "of", "the"];
    return name
     // remove text inside brackets
      .replace(/\(.*?\)/g, "")
      .replace(/[-_/]/g, " ")
      .split(" ")
      .filter((word) => word && !skipWords.includes(word.toLowerCase()))
      .map((word) => word[0].toUpperCase())
      .join("");
  };

  const shortLabels = labels.map((label) => getAbbreviation(label));
  const opacities = counts.map((_, index) => 0.9 - index * 0.05);

  const data = {
    labels: shortLabels,
    datasets: [
      {
        label: "Learners",
        data: counts,
        backgroundColor: opacities.map(
          (opacity) => `rgba(90, 87, 255, ${opacity})`
        ),
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 16,
        maxBarThickness: 18,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: false },
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const courseName = labels[index] || "";
            return `${courseName}`;
          },

          label: (tooltipItem) => {
            return `Learners: ${tooltipItem.raw}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: { size: 9 },
          padding: 2,
          color: isDark ? "#eaeaea" : "#444444",
        },
        grid: { display: false },
      },
      y: {
        ticks: {
          font: { size: 9 },
          stepSize: 10,
          padding: 2,
          color: isDark ? "#eaeaea" : "#444444",
        },
        grid: { color: isDark ? "#444444" : "#f1f1f1" },
        suggestedMax: Math.max(...counts, 65),
      },
    },
  };

  const handleCourseChange = (e) => {
    setSelectedCourse(e.target.value);
  };

  if (loading) {
    return (
      <div
        className="charts"
        style={{
          height: "280px",
          display: "flex",
          flexDirection: "column",
          padding: "16px",
          color: isDark ? "#eaeaea" : "#444",
        }}
      >
        <Skeleton width="20%" height="20px" style={{ marginTop: 10 }} />
        <div style={{ marginTop: 20, width: "100%" }}>
          <Skeleton shape="rectangle" width="100%" height="210px" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="charts"
        style={{
          height: "280px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "16px",
          color: "red",
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className="charts"
      style={{
        height: "280px",
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        color: isDark ? "#eaeaea" : "#000",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <h4 style={{ fontSize: "14px" }}>
       {selectedCourse === "All"
        ? "Learners"
        : `Total Learners (${totalLearners})`}
      </h4>
       <div
          style={{ display: "inline-block", maxWidth: "100px" }}
          title={`${courses.find(c => c.courseCode === selectedCourse)?.courseName || "All"}`}
        >
          <select
            value={selectedCourse}
            onChange={handleCourseChange}
            style={{
              maxWidth: "100px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "4px 8px",
              fontSize: "12px",
              borderRadius: "4px",
              border: `1px solid ${isDark ? "#444" : "#ccc"}`,
              backgroundColor: isDark ? "#333" : "#fff",
              color: isDark ? "#eaeaea" : "#000",
              cursor: "pointer",
              width: "100%",
            }}
          >
            {courses.map((course) => (
              <option
                key={course.courseCode}
                value={course.courseCode}
              >
                {course.courseName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ flexGrow: 1 }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
}
